import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import graduationIcon from '@iconify-icons/lucide/graduation-cap';
import searchIcon from '@iconify-icons/lucide/search';
import trophyIcon from '@iconify-icons/lucide/trophy';
import bookOpenIcon from '@iconify-icons/lucide/book-open';
import logoutIcon from '@iconify-icons/lucide/log-out';
import logInIcon from '@iconify-icons/lucide/log-in';

import { AuthContext } from '../../../context/context';
import { IboContext } from '../../../context/iboContext';
import { ProspectContext } from '../../../context/prospectContext';
import useProspect from '../../../hooks/useProspect';
import ModalHomeProspect from '../../components/prospect/ModalHomeProspect';
import logoWhite from '../../../resources/images/academyFinalWhite.png';

/**
 * Navbar del interior logueado (Academy Rediseño.dc.html líneas 62-117).
 *
 * Barra navy 72px sticky con:
 *   - Wordmark "Academy" en blanco (izquierda),
 *   - 3 tabs: Aprender · Explorar · Logros (con iconos de lucide),
 *   - a la derecha: avatar con iniciales + menú perfil (si hay sesión)
 *     o botón "Iniciar sesión" (sin sesión).
 *
 * "Logros" apunta a `/logros` — vista dedicada de cursos completados con
 * opción de descargar el certificado. Consume `getCompletedCoursesByUser`
 * (Q11-sexies) al montar.
 */
export default function AppTopNav() {
  const { userInfoQursiva, logOut } = useContext(AuthContext);
  /* Defensive: en tests los providers pueden no estar envueltos —
     el context devuelve undefined y destructurarlo crashea. */
  const { prospectInfo, clearProspectInfo } =
    useContext(ProspectContext) || {};
  /* `iboInfo` consumido del context (no de localStorage) para que
     el botón "Salir" del chip de invitado dispare re-render vía
     `clearIboInfo`. Antes se leía directo del storage y limpiar el
     storage no notificaba a React → botón sin efecto visible. */
  const { iboInfo, clearIboInfo } = useContext(IboContext) || {};
  /* Cuatro estados:
     - `isLogged` — SESIÓN REAL de un IBO (`userInfoQursiva` con token).
       Avatar con SUS iniciales + menú Mis cursos/Perfil/Cerrar sesión.
     - `isProspect` — visitante que dejó email + nombre + apellido en el
       gate del prospecto (`contact` en localStorage, expuesto por
       `ProspectContext`). Ve su avatar con SUS iniciales + menú Mis
       cursos/Salir. Diferencia con `isLogged`: no tiene token, no
       puede comprar/pagar; el CTA de compra se le sigue ocultando.
     - `isGuest` — código IBO puesto pero SIN datos del prospecto.
       Chip "Invitado · Referido por X" + Salir + Ingresar (abre el
       modal para completar los datos).
     - resto: anónimo, solo "Iniciar sesión". */
  const isLogged = Boolean(userInfoQursiva);
  const isProspect = !isLogged && Boolean(prospectInfo);
  const isGuest = !isLogged && !isProspect && Boolean(iboInfo);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('global');
  const [profileOpen, setProfileOpen] = useState(false);
  const menuRef = useRef(null);

  /* Gate del prospecto — se abre desde el CTA "Ingresar" del invitado.
     Mismo modal que /courses, /courses-details y el home. Se monta al
     final del componente (portal). Sin sesión y sin código IBO no
     tiene sentido abrirlo, pero el hook se instancia igual (barato)
     para no romper el orden de hooks. */
  const {
    handleSubmit: prospectSubmit,
    isVisibleProspect,
    toggleModalProspect,
    checkEmailInBD,
  } = useProspect(t);

  useEffect(() => {
    if (!profileOpen) return;
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [profileOpen]);

  // Cerrar el menú al navegar (por ejemplo, al pulsar "Mis cursos" o
  // "Perfil"): si no, el dropdown queda abierto sobre la pantalla nueva.
  useEffect(() => {
    setProfileOpen(false);
  }, [location.pathname]);

  const isLearn =
    location.pathname === '/' || location.pathname.startsWith('/mylearning');
  const isExplore = location.pathname.startsWith('/courses');
  const isAchievements = location.pathname.startsWith('/logros');

  /* Las tabs "Explorar" y "Logros" sólo tienen sentido cuando el
     visitante ya se identificó de algún modo (sesión, prospecto o
     código IBO). Para un anónimo puro el catálogo no es accesible
     hasta que resuelva el ModalHome del `/`, así que ni siquiera se
     ofrecen esas tabs — evita "opciones muertas" en la barra. */
  const showAppTabs = isLogged || isProspect || isGuest;

  /* Datos del usuario para pintar el avatar y el menú: primero el
     logueado (userInfoQursiva), luego el prospecto (contact). Nunca
     usamos las iniciales del IBO referente (iboInfo). */
  const user = isLogged
    ? userInfoQursiva
    : isProspect
    ? prospectInfo
    : null;
  const initials = user
    ? (user.firstName?.[0] ?? 'U') + (user.lastName?.[0] ?? '')
    : '';
  const displayName = user
    ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
    : '';
  const displayEmail = user?.email ?? '';

  /* Foto de perfil del IBO desde el CDN de lyra. Solo aplica cuando hay
     sesión real (isLogged) y el payload trae `code`. Para prospectos y
     anónimos no hay foto que buscar — se mantiene el avatar de iniciales.
     Si el <img> falla (404 porque el IBO no subió foto, red fuera, etc.),
     onError marca `avatarError` y se cae al avatar de iniciales sin ruido
     visual. El reset en el useEffect evita arrastrar un error de un IBO
     anterior cuando se cambia de sesión. */
  const iboCode = isLogged ? userInfoQursiva?.code : null;
  const avatarUrl = iboCode
    ? `https://cdn1.visiontravel.net/lyra/distributor/avatar/profile/${iboCode}.jpeg`
    : null;
  const [avatarError, setAvatarError] = useState(false);
  useEffect(() => {
    setAvatarError(false);
  }, [iboCode]);
  const showAvatarImage = Boolean(avatarUrl) && !avatarError;
  /* Nombre del IBO referente (solo para el chip de invitado). */
  const referrerName =
    iboInfo?.firstName || iboInfo?.name || iboInfo?.lastName || '';

  const tabStyle = (active) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    minHeight: 48,
    /* ACADEMY-34 #7 — antes `0 16px` fijo. En sesión de invitado el
       chip "Referido por X" (oculto en mobile) desaparece pero el
       CTA "Ingresar" con texto de ~110 px queda ocupando la
       derecha y el `<nav>` con 3 tabs × (16 padding + 20 icono) se
       desbordaba en viewports < 400 px → los iconos "Aprender" y
       "Logros" salían de pantalla detrás del botón blanco. Ahora
       las tabs tienen padding compacto por defecto y crecen desde
       sm+. Además `flexShrink: 0` en cada `<Link>` evita que un
       flex parent con espacio limitado los recorte a 0. */
    padding: '0 10px',
    flexShrink: 0,
    border: 0,
    background: active ? 'rgba(255, 255, 255, 0.12)' : 'transparent',
    color: '#fff',
    borderRadius: 10,
    fontSize: 15,
    fontWeight: active ? 800 : 600,
    cursor: 'pointer',
    textDecoration: 'none',
  });

  // En móvil los labels de las tabs se ocultan (`hidden sm:inline`) para que
  // solo quede el icono. El mockup `mobileHead` no tiene tabs, pero conservar
  // la navegación icon-only es más útil que retirarla en pantallas pequeñas.

  return (
    <>
    <header
      style={{
        background: 'var(--color-ink)',
        position: 'sticky',
        top: 0,
        zIndex: 50,
      }}
      data-slot="app-top-nav"
    >
      <div
        className="mx-auto px-s3 sm:px-s6"
        style={{
          maxWidth: 1160,
          height: 72,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <Link
          to="/"
          aria-label="Academy by VisionBiz"
          style={{ display: 'block', flex: '0 0 auto', lineHeight: 0 }}
        >
          <img
            src={logoWhite}
            alt="Academy by VisionBiz"
            style={{ height: 30, width: 'auto', display: 'block' }}
          />
        </Link>

        <nav
          style={{
            display: 'flex',
            gap: 4,
            flex: 1,
            /* Sin `minWidth: 0` el `<nav>` no puede achicarse por debajo
               de su contenido intrínseco — inutiliza `flex: 1` cuando
               los hermanos (logo + botón derecho) piden más de lo que
               cabe. Con `minWidth: 0` sí acepta comprimirse (los
               `<Link>` internos tienen `flexShrink: 0`, así que no se
               destruyen). ACADEMY-34 #7. */
            minWidth: 0,
          }}
          aria-label="Principal"
        >
          <Link to="/" style={tabStyle(isLearn)} aria-label="Aprender">
            <Icon icon={graduationIcon} width={20} height={20} aria-hidden="true" />
            <span className="hidden sm:inline">Aprender</span>
          </Link>
          {showAppTabs && (
            <>
              <Link to="/courses" style={tabStyle(isExplore)} aria-label="Explorar">
                <Icon icon={searchIcon} width={20} height={20} aria-hidden="true" />
                <span className="hidden sm:inline">Explorar</span>
              </Link>
              <Link to="/logros" style={tabStyle(isAchievements)} aria-label="Logros">
                <Icon icon={trophyIcon} width={20} height={20} aria-hidden="true" />
                <span className="hidden sm:inline">Logros</span>
              </Link>
            </>
          )}
        </nav>

        {isLogged || isProspect ? (
          <div style={{ position: 'relative' }} ref={menuRef}>
            <button
              type="button"
              onClick={() => setProfileOpen((v) => !v)}
              aria-label="Abrir menú de perfil"
              aria-expanded={profileOpen}
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: 'var(--color-secondary)',
                color: '#08222C',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 800,
                fontSize: 14,
                border: 0,
                padding: 0,
                cursor: 'pointer',
                textTransform: 'uppercase',
                overflow: 'hidden',
              }}
            >
              {showAvatarImage ? (
                <img
                  src={avatarUrl}
                  alt=""
                  onError={() => setAvatarError(true)}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              ) : (
                initials
              )}
            </button>

            {profileOpen && (
              <div
                role="menu"
                style={{
                  position: 'absolute',
                  top: 48,
                  right: 0,
                  width: 280,
                  background: '#fff',
                  border: '1px solid var(--color-line)',
                  borderRadius: 16,
                  boxShadow: '0 16px 40px rgba(16, 28, 54, 0.18)',
                  overflow: 'hidden',
                  zIndex: 80,
                }}
              >
                <div
                  style={{
                    padding: '18px 18px 16px',
                    display: 'flex',
                    gap: 12,
                    alignItems: 'center',
                  }}
                >
                  <div
                    style={{
                      width: 52,
                      height: 52,
                      borderRadius: '50%',
                      background: 'var(--color-secondary)',
                      color: '#08222C',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 800,
                      fontSize: 19,
                      flex: '0 0 auto',
                      textTransform: 'uppercase',
                      overflow: 'hidden',
                    }}
                  >
                    {showAvatarImage ? (
                      <img
                        src={avatarUrl}
                        alt=""
                        onError={() => setAvatarError(true)}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                    ) : (
                      initials
                    )}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 15.5,
                        fontWeight: 800,
                        color: 'var(--color-ink)',
                        lineHeight: 1.25,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {displayName}
                    </div>
                    {displayEmail && (
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: 'var(--color-ink-2)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {displayEmail}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ height: 1, background: '#E7EBF2' }} />
                <div style={{ padding: 8 }}>
                  <Link
                    to="/mylearning"
                    role="menuitem"
                    onClick={() => setProfileOpen(false)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      width: '100%',
                      minHeight: 48,
                      borderRadius: 10,
                      padding: '0 10px',
                      fontSize: 14.5,
                      fontWeight: 700,
                      color: 'var(--color-ink)',
                      textDecoration: 'none',
                    }}
                  >
                    <Icon icon={bookOpenIcon} width={19} height={19} aria-hidden="true" />
                    Mis cursos
                  </Link>
                </div>
                <div style={{ height: 1, background: '#E7EBF2' }} />
                <div style={{ padding: 8 }}>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setProfileOpen(false);
                      /* Prospecto: solo limpio `contact`; conservo el
                         `iboInfo` para no perder el tracking del
                         referente si vuelve a interactuar. Logueado:
                         logOut real que limpia todo + redirige. */
                      if (isProspect) {
                        clearProspectInfo();
                      } else {
                        logOut({ unAuthorized: true });
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      width: '100%',
                      minHeight: 48,
                      border: 0,
                      background: 'transparent',
                      borderRadius: 10,
                      padding: '0 10px',
                      fontSize: 14.5,
                      fontWeight: 700,
                      color: 'var(--color-ink-2)',
                      cursor: 'pointer',
                    }}
                  >
                    <Icon icon={logoutIcon} width={19} height={19} aria-hidden="true" />
                    {isProspect ? 'Salir' : 'Cerrar sesión'}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : isGuest ? (
          /* Invitado con código IBO — chip "Referido por X" +
             botón "Salir" (limpia iboInfo con logOut) + CTA primario
             "Ingresar" que abre el mismo `ModalHomeProspect` que se
             usa desde /courses, /courses-details y el home. El
             invitado no tiene credenciales — necesita registrarse
             como prospecto para que el IBO referente le dé
             seguimiento. */
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {referrerName && (
              /* Chip compuesto: etiqueta + separador + botón "Salir".
                 Antes eran dos elementos separados; el "Salir" flotaba
                 al lado del chip y saturaba el navbar. Ahora se integra
                 como una segunda sección del mismo pill — misma altura,
                 mismo fondo suave, con una línea divisoria semitrans-
                 parente. Sólo la parte "Salir" es interactiva. */
              <span
                className="hidden sm:inline-flex"
                style={{
                  display: 'inline-flex',
                  alignItems: 'stretch',
                  minHeight: 36,
                  borderRadius: 999,
                  background: 'rgba(255, 255, 255, 0.12)',
                  color: '#fff',
                  overflow: 'hidden',
                }}
              >
                <span
                  title={`Estás como invitado, referido por ${referrerName}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '0 14px',
                    fontSize: 12.5,
                    fontWeight: 800,
                    letterSpacing: '.02em',
                  }}
                >
                  Invitado · Referido por {referrerName}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    /* Solo limpiamos el código IBO — el visitante
                       vuelve a estado anónimo. Antes se llamaba
                       `logOut()` del AuthContext, pero como el
                       usuario nunca tuvo sesión (userInfoQursiva ya
                       era `false`), `setUserInfoQursiva(false)` no
                       disparaba re-render y el botón parecía muerto.
                       `clearIboInfo` cambia el IboContext → todos
                       los consumidores re-renderizan. */
                    clearIboInfo?.();
                  }}
                  aria-label="Salir del modo invitado"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '0 14px',
                    border: 0,
                    borderLeft: '1px solid rgba(255, 255, 255, 0.22)',
                    background: 'transparent',
                    color: '#fff',
                    fontSize: 12.5,
                    fontWeight: 800,
                    cursor: 'pointer',
                    letterSpacing: '.02em',
                  }}
                >
                  Salir
                </button>
              </span>
            )}
            {/* Mobile-only "Salir": el chip de arriba está oculto en <sm,
                así que sin esto el invitado no tenía forma de volver a
                anónimo (y por tanto de llegar al "Iniciar sesión" de IBO).
                Icon-only para no competir con el "Ingresar" al lado. */}
            <button
              type="button"
              onClick={() => clearIboInfo?.()}
              aria-label="Salir del modo invitado"
              title="Salir del modo invitado"
              className="sm:hidden flex items-center justify-center min-h-touch border-0 cursor-pointer"
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: 'rgba(255, 255, 255, 0.12)',
                color: '#fff',
                flexShrink: 0,
                padding: 0,
              }}
            >
              <Icon icon={logoutIcon} width={19} height={19} aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={toggleModalProspect}
              aria-label="Ingresar"
              /* Compacto en mobile (`< sm`): sólo icono, sin texto.
                 En sm+ crece a "Ingresar" con padding cómodo. Evita
                 comer el ancho que necesitan los iconos del <nav>
                 en pantallas estrechas. ACADEMY-34 #7. */
              className="flex items-center min-h-touch border-0 rounded-btn font-extrabold cursor-pointer text-[15px] gap-s2 px-s3 sm:px-s4"
              style={{
                background: '#fff',
                color: 'var(--color-ink)',
                flexShrink: 0,
              }}
            >
              <Icon icon={logInIcon} width={19} height={19} aria-hidden="true" />
              <span className="hidden sm:inline">Ingresar</span>
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => navigate('/login')}
              aria-label="Iniciar sesión"
              /* Mismo tratamiento compacto que el CTA de invitado
                 (ACADEMY-34 #7): sólo icono en mobile, texto en sm+. */
              className="flex items-center min-h-touch border-0 rounded-btn font-extrabold cursor-pointer text-[15px] gap-s2 px-s3 sm:px-s4"
              style={{
                background: '#fff',
                color: 'var(--color-ink)',
                flexShrink: 0,
              }}
            >
              <Icon icon={logInIcon} width={19} height={19} aria-hidden="true" />
              <span className="hidden sm:inline">Iniciar sesión</span>
            </button>
          </div>
        )}
      </div>
    </header>
    <ModalHomeProspect
      isVisibleProspect={isVisibleProspect}
      toggleModalProspect={toggleModalProspect}
      handleSubmit={prospectSubmit}
      checkEmailInBD={checkEmailInBD}
    />
    </>
  );
}
